import React, { useEffect, useState } from 'react';

import { ButtonUI, DatagridPremiumUi, DialogUI } from '../Interface';
import { dataGridPremiumColumns } from '../../utils/helper';
import dayjs from 'dayjs';
import { Box } from '@mui/material';
import { DocStatus } from '../../config/document';
import { DocumentType } from '../../config/DocumentType';
import documentService from '../../service/newDoc.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const SourceDocTableDialog = (props) => {
  const { open, onClose, docName, data, sourceDatagridIdentifiers, setSelectedDocRows, setDocLineTableDialogOpen } =
    props;
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  useEffect(() => {
    setRowSelectionModel(data.rows.map((row) => row.docId));
  }, [data.rows, open]);

  const columns = dataGridPremiumColumns([
    {
      field: 'docNum',
      headerName: 'Bizonylatszám',
      type: 'string',
      filterable: false,
    },
    {
      field: 'reference',
      headerName: 'Hivatkozási szám',
      type: 'string',
      filterable: false,
    },
    {
      field: 'docDate',
      headerName: 'Teljesítés dátuma',
      type: 'date',
      filterable: false,
      valueGetter: (_data, row) => {
        return new Date(row.docDate);
      },
      renderCell: (data) => {
        return dayjs(data.row.docDate).format('YYYY-MM-DD');
      },
    },
    {
      field: 'issueDate',
      headerName: 'Kiállítás dátuma',
      type: 'date',
      filterable: false,
      valueGetter: (_data, row) => {
        return new Date(row.issueDate);
      },
      renderCell: (data) => {
        return dayjs(data.row.issueDate).format('YYYY-MM-DD');
      },
    },
    {
      field: 'dueDate',
      headerName: 'Határidő',
      type: 'date',
      filterable: false,
      valueGetter: (_data, row) => {
        return new Date(row.dueDate);
      },
      renderCell: (data) => {
        return dayjs(data.row.dueDate).format('YYYY-MM-DD');
      },
    },
    {
      field: 'companyShortName',
      headerName: 'Rövid név',
      type: 'string',
      filterable: false,
      renderCell: (data) => {
        return data.row.company?.shortName;
      },
    },
    {
      field: 'netInDocCur',
      headerName: 'Nettó érték',
      type: 'number',
      filterable: false,
    },
    {
      field: 'currencyCode',
      headerName: 'Pénznem',
      type: 'string',
      filterable: false,
    },
    {
      field: 'projectId',
      headerName: 'Projekt',
      type: 'string',
      filterable: false,
      renderCell: (data) => {
        return data.row.project?.projectName;
      },
    },
    {
      field: 'costCenterCode',
      headerName: 'Költséghely',
      type: 'string',
      filterable: false,
    },
    {
      field: 'deliveryMethodId',
      headerName: 'Szállítási mód',
      type: 'string',
      filterable: false,
      renderCell: (data) => {
        return data.row.deliveryMethod?.name;
      },
    },
    {
      field: 'shippingAdressId',
      headerName: 'Szállítási cím',
      type: 'string',
      filterable: false,
      renderCell: (data) => {
        return data.row.shippingAdress?.name;
      },
    },
  ]);

  const handleSelectRows = async () => {
    const selectedRows = data.rows.filter((row) => rowSelectionModel.includes(row.docId));

    try {
      const sourceRows = await Promise.all(
        selectedRows.map(async (row) => {
          if (parseInt(row.docTypeId) !== DocumentType.CustomerOrder) return row;

          const sourceDocument = await documentService.getDocumentById(row.docId, row.docTypeId);
          return { ...row, ...sourceDocument };
        })
      );

      const docLine = sourceRows.flatMap((row) =>
        (row.docLine || [])
          .filter((line) => line.lineStatus === DocStatus.OPENED)
          .map((line) => ({
            ...line,
            reference: row.reference,
            docNum: row.docNum,
            currencyCode: row.currencyCode,
            baseDocnum: row.docNum,
            baseId: row.docId,
            baseDocType: row.docTypeId,
          }))
      );

      const firstDoc = sourceRows.reduce((min, row) => (row.docId < min.docId ? row : min), sourceRows[0]);

      const reference = [...new Set(sourceRows.map((row) => row.reference))].join('; ');

      const noteList = sourceRows.map((row) => {
        return `${row.docNum}`;
      });
      const note = `Forrása: ${docName} ${noteList.join(', ')}`;
      const supportingDocumentAssignment = sourceRows.flatMap((row) => row.supportingDocumentAssignment ?? []);

      setSelectedDocRows({
        ...firstDoc,
        reference,
        note,
        docLine,
        supportingDocumentAssignment,
      });
      setDocLineTableDialogOpen(true);
      onClose();
    } catch {
      addNotification({
        content: 'Hiba történt a forrásbizonylat adatainak lekérésekor!',
        type: NotificationType.ERROR,
      });
    }
  };

  return (
    <DialogUI open={open} onClose={onClose} headerContent={docName} maxWidth="xl" fullWidth>
      <DatagridPremiumUi
        datagridIdentifier={sourceDatagridIdentifiers.sourceDoc}
        datagridInitialState={{ density: 'compact' }}
        rows={data.rows}
        hideFooter
        height={700}
        columns={columns}
        headerFilters={false}
        getRowId={(row) => row.docId}
        paginationAndfilterMode="client"
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(newModel) => setRowSelectionModel(newModel)}
      />

      <Box display="flex" justifyContent="center" gap="0.5rem" mt="1rem">
        <ButtonUI
          text="Kiválasztás"
          className="bg-success"
          onClick={handleSelectRows}
          disabled={rowSelectionModel?.length === 0}
        />
        <ButtonUI text="Mégsem" className="bg-primary" onClick={onClose} />
      </Box>
    </DialogUI>
  );
};

export default SourceDocTableDialog;
